import {getUid, isUnauthorizedUser, syncCustomerIdToStoredUid} from "./anonymousUid";
import {
    buildOrganizationCompositionModulePatchBody,
    getModuleCompositionDimension,
} from "./mapModuleCompositionToModuleItem";

export {getModuleCompositionDimension} from "./mapModuleCompositionToModuleItem";

export const CATALOG_ITEM_NARROW_BREAKPOINT = 1023;
export const catalogItemNarrowMq = `@media (max-width: ${CATALOG_ITEM_NARROW_BREAKPOINT}px)`;
export const catalogItemNarrowMediaQuery = `(max-width: ${CATALOG_ITEM_NARROW_BREAKPOINT}px)`;

export const CATALOG_ITEM_MODULE_ROW_STACK_BREAKPOINT = 860;
export const catalogItemModuleRowStackMq = `@media (max-width: ${CATALOG_ITEM_MODULE_ROW_STACK_BREAKPOINT}px)`;
export const catalogItemModuleRowStackMediaQuery = `(max-width: ${CATALOG_ITEM_MODULE_ROW_STACK_BREAKPOINT}px)`;

export const CATALOG_ITEM_MEDIA_STACK_BREAKPOINT = 1440;
export const catalogItemMediaStackMq = `@media (max-width: ${CATALOG_ITEM_MEDIA_STACK_BREAKPOINT}px)`;
export const catalogItemMediaStackMediaQuery = `(max-width: ${CATALOG_ITEM_MEDIA_STACK_BREAKPOINT}px)`;

export const CATALOG_ITEM_GALLERY_STACK_BREAKPOINT = 1320;
export const catalogItemGalleryStackMq = `@media (max-width: ${CATALOG_ITEM_GALLERY_STACK_BREAKPOINT}px)`;
export const catalogItemGalleryStackMediaQuery = `(max-width: ${CATALOG_ITEM_GALLERY_STACK_BREAKPOINT}px)`;

export const isPrimaryMediaModule = (row) =>
    getParentModuleCompositionId(row) == null;

export const getModuleCompositionHeightLocation = (row) =>
    row?.module?.heightLocation ?? row?.heightLocation;

/** Customer catalog/order: TOP always; BOTTOM when module has blockSize. */
export const isCustomerModuleWidthLocked = (row) => {
    if (getModuleCompositionHeightLocation(row) === "TOP") {
        return true;
    }
    return Boolean(row?.module?.blockSize ?? row?.blockSize);
};

const isOrgPatternOnlyDependentPayload = (raw) => {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
        return false;
    }
    if (raw.moduleComposition?.id != null) {
        return false;
    }
    return raw.patternW != null || raw.patternH != null || raw.patternD != null;
};

/**
 * Normalize module rows for media card grouping to match /catalog/:id «Список шкафов».
 *
 * Customer/catalog payload:
 *   dependentModuleComposition: { id: <patternId>, moduleComposition: { id: <parentRowId> } }
 *
 * Org payload often has only the pattern object (`id` + patternW/H/D) without
 * `moduleComposition`. Falling back to pattern `id` as parent mis-assigns sections.
 * In that case rebuild parent links like catalog: TOP → BOTTOM with same side + priority,
 * with unmatched TOP falling back to the corner BOTTOM (catalog composition/3 case).
 */
export const normalizeModuleCompositionsForMedia = (modules) => {
    const list = Array.isArray(modules) ? modules.map((row) => ({...row})) : [];
    if (list.length === 0) {
        return list;
    }

    const hasCatalogParentLink = (row) =>
        row?.dependentModuleComposition?.moduleComposition?.id != null;

    if (list.some(hasCatalogParentLink)) {
        return list;
    }

    const bottoms = list.filter((row) => row?.module?.heightLocation !== "TOP");

    const findBottomParent = (row) => {
        const sameSideSamePriority = bottoms.find(
            (bottom) =>
                bottom.sideBaseModule === row.sideBaseModule &&
                bottom.priority === row.priority,
        );
        if (sameSideSamePriority) {
            return sameSideSamePriority;
        }
        if (isCornerSideBaseModule(row.sideBaseModule)) {
            return (
                bottoms.find(
                    (bottom) =>
                        isCornerSideBaseModule(bottom.sideBaseModule) &&
                        bottom.priority === row.priority,
                ) ||
                bottoms.find((bottom) => isCornerSideBaseModule(bottom.sideBaseModule))
            );
        }
        // e.g. RIGHT TOP without matching RIGHT BOTTOM → corner BOTTOM (catalog/3)
        return bottoms.find((bottom) => isCornerSideBaseModule(bottom.sideBaseModule));
    };

    return list.map((row) => {
        if (row?.module?.heightLocation !== "TOP") {
            return {
                ...row,
                dependentModuleComposition: null,
            };
        }
        const parent = findBottomParent(row);
        if (!parent?.id) {
            return {
                ...row,
                dependentModuleComposition: null,
            };
        }
        return {
            ...row,
            dependentModuleComposition: {
                moduleComposition: {id: parent.id},
            },
        };
    });
};

/** Parent id for a dependent row: dependentModuleComposition.moduleComposition.id */
export const getParentModuleCompositionId = (row) => {
    const raw = row?.dependentModuleComposition;
    if (raw == null) {
        return null;
    }
    const parentId = raw?.moduleComposition?.id;
    if (parentId != null) {
        return parentId;
    }
    // Org pattern entity id is not a parent moduleComposition id.
    if (isOrgPatternOnlyDependentPayload(raw)) {
        return null;
    }
    if (typeof raw === "object" && !Array.isArray(raw) && raw.id != null) {
        return raw.id;
    }
    if (typeof raw === "number" || typeof raw === "string") {
        return raw;
    }
    return null;
};

export const normalizeModuleCompositionId = (id) =>
    id == null ? null : String(id);

/**
 * Строка списка шкафов для подсветки и поля «Ширина»:
 * у верхнего (TOP) — нижний родитель, у остальных — сам модуль.
 */
export const resolveMediaModulePanelAnchorRowId = (row, compositionList = null) => {
    if (row == null) {
        return null;
    }
    const rowKey = normalizeModuleCompositionId(row.id);
    const heightLocation = row?.module?.heightLocation ?? row?.heightLocation;
    if (heightLocation !== "TOP") {
        return rowKey;
    }
    const parentId = normalizeModuleCompositionId(getParentModuleCompositionId(row));
    if (parentId != null) {
        return parentId;
    }
    const list = Array.isArray(compositionList) ? compositionList : [];
    const self = list.find(
        (item) => normalizeModuleCompositionId(item.id) === rowKey,
    );
    const fromList = normalizeModuleCompositionId(
        getParentModuleCompositionId(self ?? row),
    );
    return fromList ?? rowKey;
};

/** Template module id referenced by a draft module row. */
export const getOriginalModuleCompositionId = (row) => {
    const raw = row?.originalModuleComposition;
    if (raw == null) {
        return null;
    }
    if (typeof raw === "object" && !Array.isArray(raw) && raw.id != null) {
        return raw.id;
    }
    if (typeof raw === "number" || typeof raw === "string") {
        return raw;
    }
    return null;
};

export const sortModuleCompositionsByPriority = (a, b) => {
    const pa = Number(a?.priority);
    const pb = Number(b?.priority);
    const aHasPriority = Number.isFinite(pa);
    const bHasPriority = Number.isFinite(pb);

    if (aHasPriority && bHasPriority) {
        return pa - pb;
    }
    if (aHasPriority) {
        return -1;
    }
    if (bHasPriority) {
        return 1;
    }
    return 0;
};

const getSpecificationModuleHeightLocationSortOrder = (row) =>
    row?.module?.heightLocation === "TOP" || row?.heightLocation === "TOP" ? 1 : 0;

/** Specification table: lower (BOTTOM) modules first, then TOP; within group by priority. */
export const sortSpecificationModuleCompositions = (a, b) => {
    const locationDiff =
        getSpecificationModuleHeightLocationSortOrder(a)
        - getSpecificationModuleHeightLocationSortOrder(b);
    if (locationDiff !== 0) {
        return locationDiff;
    }
    return sortModuleCompositionsByPriority(a, b);
};

export const buildMediaModuleSideGroup = (primaryCandidates, allModuleCompositions) => {
    const primaries = Array.isArray(primaryCandidates) ? primaryCandidates : [];
    const all = Array.isArray(allModuleCompositions)
        ? allModuleCompositions
        : primaries;

    const primary = primaries
        .filter(isPrimaryMediaModule)
        .slice()
        .sort(sortModuleCompositionsByPriority);
    const dependentsByParentId = new Map();
    for (const row of all) {
        if (isPrimaryMediaModule(row)) {
            continue;
        }
        const parentId = normalizeModuleCompositionId(
            getParentModuleCompositionId(row),
        );
        if (parentId == null) {
            continue;
        }
        if (!dependentsByParentId.has(parentId)) {
            dependentsByParentId.set(parentId, []);
        }
        dependentsByParentId.get(parentId).push(row);
    }
    return {primary, dependentsByParentId};
};

export const isCornerSideBaseModule = (side) =>
    side === "CORNER_LEFT" ||
    side === "CORNER__LEFT" ||
    side === "CORNER_RIGHT" ||
    side === "CORNER__RIGHT";

export const MEDIA_MODULE_MARKER_SIZE = 40;
export const MEDIA_MODULE_MARKER_CORE_SIZE = 7;
/** Кольцо маркера на карте 2D (настройки композиции и каталог). */
export const MEDIA_MODULE_MARKER_RING_WIDTH = 13;
export const MEDIA_MODULE_MARKER_BORDER_WIDTH =
    (MEDIA_MODULE_MARKER_SIZE - MEDIA_MODULE_MARKER_CORE_SIZE) / 2;
export const MEDIA_IMAGE_DEFAULT_WIDTH = 730;
export const MEDIA_IMAGE_DEFAULT_HEIGHT = 716;

export const resolveDependentModuleRows = (primaryRow, compositionList) => {
    if (!isPrimaryMediaModule(primaryRow)) {
        return [];
    }
    const parentId = primaryRow?.id;
    if (parentId == null) {
        return [];
    }
    return getDependentModuleCompositionsByParentId(parentId, compositionList);
};

export const getModuleCompositionDisplayName = (row) => {
    if (row?.name) {
        return row.name;
    }
    if (row?.module?.name) {
        return row.module.name;
    }
    if (row?.id != null) {
        return `Модуль #${row.id}`;
    }
    return "—";
};

const formatModuleCompositionDimensionValue = (value) =>
    value == null || value === "" ? "—" : String(value);

/** Width field label for a row in the media module panel (matches CompositionMediaModuleRow). */
export const getModuleCompositionPanelWidthLabel = (row, cornerType) => {
    if (isCornerSideBaseModule(row?.sideBaseModule)) {
        return getCornerModulePrimaryRowConfig(resolveCornerTypeForCornerModuleRow(row, cornerType)).label;
    }
    if (cornerType === "STRAIGHT") {
        return "Длина";
    }
    if (row?.sideBaseModule === "LEFT") {
        return "Слева";
    }
    if (row?.sideBaseModule === "RIGHT") {
        return "Справа";
    }
    return "Ширина";
};

/** Accusative dimension label in width-change confirm dialog (always «длину»). */
export const getModuleCompositionPanelDimensionLabelAccusative = () => "длину";

/** Single size shown in the module panel row (width only for dependents). */
export const formatModuleCompositionPanelSize = (row, cornerType) => {
    if (isCornerSideBaseModule(row?.sideBaseModule)) {
        return getCornerModuleRowConfigs(resolveCornerTypeForCornerModuleRow(row, cornerType))
            .map(({label, sizeField}) => {
                const value = formatModuleCompositionDimensionValue(
                    getModuleCompositionDimension(row, sizeField),
                );
                return `${label}: ${value}${value === "—" ? "" : " мм"}`;
            })
            .join(", ");
    }
    const label = getModuleCompositionPanelWidthLabel(row, cornerType);
    const value = formatModuleCompositionDimensionValue(
        getModuleCompositionDimension(row, "width"),
    );
    return `${label}: ${value}${value === "—" ? "" : " мм"}`;
};

/** Current panel size for confirm dialog: «сейчас длина 600 мм». */
export const formatModuleCompositionPanelSizeDialogCurrent = (row) => {
    const value = formatModuleCompositionDimensionValue(
        getModuleCompositionDimension(row, "width"),
    );
    return `сейчас длина ${value}${value === "—" ? "" : " мм"}`;
};

/** All row ids that may refer to the same module row (template, draft, original). */
export const collectModuleCompositionRowIdentityKeys = (row) => {
    const keys = new Set();
    const add = (value) => {
        const key = normalizeModuleCompositionId(value);
        if (key != null) {
            keys.add(key);
        }
    };
    if (row == null) {
        return keys;
    }
    add(row.id);
    add(row.moduleCompositionId);
    add(row[CATALOG_DRAFT_MODULE_COMPOSITION_ID_FIELD]);
    add(getOriginalModuleCompositionId(row));
    add(row.originalModuleCompositionId);
    return keys;
};

const findModuleCompositionRowByIdentityKey = (identityKey, compositionList) => {
    const key = normalizeModuleCompositionId(identityKey);
    if (key == null) {
        return null;
    }
    const list = Array.isArray(compositionList) ? compositionList : [];
    return list.find((row) => collectModuleCompositionRowIdentityKeys(row).has(key)) ?? null;
};

/** Template/list row id for any identity key (draft parent links → template id). */
export const resolveModuleCompositionCanonicalRowId = (rowId, compositionList) => {
    const row = findModuleCompositionRowByIdentityKey(rowId, compositionList);
    const canonical = normalizeModuleCompositionId(row?.id);
    return canonical ?? normalizeModuleCompositionId(rowId);
};

const getCanonicalParentModuleCompositionId = (row, compositionList) => {
    const parentId = getParentModuleCompositionId(row);
    if (parentId == null) {
        return null;
    }
    return resolveModuleCompositionCanonicalRowId(parentId, compositionList);
};

const normalizeModuleCompositionsForHover = (compositionList) =>
    normalizeModuleCompositionsForMedia(
        Array.isArray(compositionList) ? compositionList : [],
    );

/**
 * Id строк состава для подсветки на сцене/маркерах при hover/focus поля ширины:
 * сам модуль, родитель (для dependent/TOP) и все зависимые от того же родителя.
 */
export const resolveModuleCompositionHoverRowIds = (hoveredRowId, compositionList) => {
    const list = normalizeModuleCompositionsForHover(compositionList);
    const hoveredKey = resolveModuleCompositionCanonicalRowId(hoveredRowId, list);
    if (hoveredKey == null) {
        return new Set();
    }
    const hoveredRow = findModuleCompositionRowByIdentityKey(hoveredRowId, list);
    if (!hoveredRow) {
        return new Set([hoveredKey]);
    }
    const parentKey = getCanonicalParentModuleCompositionId(hoveredRow, list)
        ?? hoveredKey;
    const ids = new Set([hoveredKey, parentKey]);
    getDependentModuleCompositionsByParentId(parentKey, list).forEach((dep) => {
        const depKey = normalizeModuleCompositionId(dep?.id);
        if (depKey != null) {
            ids.add(depKey);
        }
    });
    return ids;
};

/** Hover id set including draft/original keys for 3D scene row matching. */
export const resolveModuleCompositionHoverIdentityKeys = (hoveredRowId, compositionList) => {
    const list = normalizeModuleCompositionsForHover(compositionList);
    const logical = resolveModuleCompositionHoverRowIds(hoveredRowId, list);
    const expanded = new Set(logical);
    for (const row of list) {
        const canonicalId = normalizeModuleCompositionId(row?.id);
        if (canonicalId != null && logical.has(canonicalId)) {
            collectModuleCompositionRowIdentityKeys(row).forEach((key) => {
                expanded.add(key);
            });
        }
    }
    return expanded;
};

export const isModuleCompositionRowHoverHighlighted = (
    rowId,
    hoveredRowId,
    compositionList,
) => {
    if (rowId == null || hoveredRowId == null) {
        return false;
    }
    const list = normalizeModuleCompositionsForHover(compositionList);
    const hoverKeys = resolveModuleCompositionHoverIdentityKeys(hoveredRowId, list);
    const row = findModuleCompositionRowByIdentityKey(rowId, list) ?? {id: rowId};
    return [...collectModuleCompositionRowIdentityKeys(row)].some((key) => hoverKeys.has(key));
};

/**
 * Id строки списка шкафов, в чьё поле «Ширина» ставить фокус (редактируемый модуль группы).
 */
export const resolveModuleCompositionEditableWidthRowId = (
    moduleRow,
    compositionList,
    {useCustomerWidthLockRules = false} = {},
) => {
    const list = normalizeModuleCompositionsForHover(compositionList);
    const rowKey = normalizeModuleCompositionId(
        moduleRow != null && typeof moduleRow === "object" ? moduleRow.id : moduleRow,
    );
    if (rowKey == null) {
        return null;
    }
    const row = findModuleCompositionRowByIdentityKey(rowKey, list);
    if (!row) {
        return null;
    }
    const canEdit = (candidate) =>
        !useCustomerWidthLockRules || !isCustomerModuleWidthLocked(candidate);
    const groupIds = resolveModuleCompositionHoverRowIds(row.id, list);
    const inGroup = list.filter((item) =>
        groupIds.has(normalizeModuleCompositionId(item.id)),
    );
    const primaryEditable = inGroup.find(
        (item) => isPrimaryMediaModule(item) && canEdit(item),
    );
    if (primaryEditable) {
        return normalizeModuleCompositionId(primaryEditable.id);
    }
    const anyEditable = inGroup.find((item) => canEdit(item));
    return anyEditable ? normalizeModuleCompositionId(anyEditable.id) : null;
};

/** Direct dependents of any composition row (by moduleCompositions row id). */
export const getDependentModuleCompositionsByParentId = (parentRowId, compositionList) => {
    const list = normalizeModuleCompositionsForHover(compositionList);
    const parentKey = resolveModuleCompositionCanonicalRowId(parentRowId, list);
    if (parentKey == null) {
        return [];
    }
    return list.filter(
        (row) => getCanonicalParentModuleCompositionId(row, list) === parentKey,
    );
};

export const collectMediaModuleMarkers = (compositionList) => {
    const list = Array.isArray(compositionList) ? compositionList : [];
    const forMediaCard = list.filter((m) => m?.module?.heightLocation !== "TOP");
    const seen = new Set();
    const markers = [];

    const addMarker = (row) => {
        if (!row?.id || seen.has(row.id)) {
            return;
        }
        const x = row.coordinateXPoint;
        const y = row.coordinateYPoint;
        if (x == null || y == null) {
            return;
        }
        const nx = Number(x);
        const ny = Number(y);
        if (Number.isNaN(nx) || Number.isNaN(ny)) {
            return;
        }
        seen.add(row.id);
        markers.push(row);
    };

    forMediaCard.forEach((primaryRow) => {
        addMarker(primaryRow);
        const deps = resolveDependentModuleRows(primaryRow, list);
        if (Array.isArray(deps)) {
            deps.forEach(addMarker);
        }
    });

    return markers;
};

export const getMediaModuleGroups = (moduleCompositions, {includeTop = false} = {}) => {
    const list = Array.isArray(moduleCompositions) ? moduleCompositions : [];
    const forMediaCard = includeTop
        ? list
        : list.filter((m) => m?.module?.heightLocation !== "TOP");
    const corner = forMediaCard.filter((m) => isCornerSideBaseModule(m.sideBaseModule));
    const left = forMediaCard.filter((m) => m.sideBaseModule === "LEFT");
    const right = forMediaCard.filter((m) => m.sideBaseModule === "RIGHT");
    return {
        corner: buildMediaModuleSideGroup(corner, list),
        left: buildMediaModuleSideGroup(left, list),
        right: buildMediaModuleSideGroup(right, list),
    };
};

export const scheduleMediaMarkersRender = (callback) => {
    if (typeof window.requestIdleCallback === "function") {
        return window.requestIdleCallback(callback, {timeout: 300});
    }
    return window.setTimeout(callback, 0);
};

export const cancelMediaMarkersRender = (taskId) => {
    if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(taskId);
        return;
    }
    window.clearTimeout(taskId);
};

/**
 * Guest `key` for catalog module POST; `null` for authorized users.
 */
export const isCatalogGuestUser = isUnauthorizedUser;

export const getCatalogModuleChangeKeyIfAnonymous = () => {
    return isCatalogGuestUser() ? getUid() : null;
};

export const CATALOG_DRAFT_MODULE_COMPOSITION_ID_FIELD = "draftModuleCompositionId";

/** `first` composition id — path `originalId` for module width POST. */
export const resolveCatalogModulePatchQueryOriginalId = (template) => {
    if (template?.id == null || template.id === "") {
        return null;
    }
    return String(template.id);
};

/** `second` composition id — optional `id` query param for module width POST. */
export const resolveCatalogModulePatchQueryId = (draft) =>
    extractCatalogCompositionDraftId(draft);

/** @deprecated use resolveCatalogModulePatchQueryId */
export const resolveCatalogModulePatchPathCompositionId = resolveCatalogModulePatchQueryId;

/** Template module id — `originalId` in POST body. */
export const getCatalogModuleWidthPatchBodyOriginalId = (compositionModule) => {
    const templateModuleId = getOriginalModuleCompositionId(compositionModule);
    if (templateModuleId != null && templateModuleId !== "") {
        return String(templateModuleId);
    }
    if (compositionModule?.id == null || compositionModule.id === "") {
        return null;
    }
    return String(compositionModule.id);
};

/** POST body for POST /customers/compositions/{originalId} (смена ширины модуля). */
export const buildCompositionModuleWidthPatchPayload = (compositionModule, width) => {
    const templateRowOriginalId = getCatalogModuleWidthPatchBodyOriginalId(compositionModule);
    if (templateRowOriginalId == null) {
        return null;
    }
    const numericWidth = Number(width);
    const numericHeight = Number(getModuleCompositionDimension(compositionModule, "height"));
    const numericDepth = Number(getModuleCompositionDimension(compositionModule, "depth"));
    if (
        !Number.isFinite(numericWidth)
        || !Number.isFinite(numericHeight)
        || !Number.isFinite(numericDepth)
    ) {
        return null;
    }
    return {
        width: numericWidth,
        height: numericHeight,
        depth: numericDepth,
        originalId: Number(templateRowOriginalId),
        additional: Boolean(compositionModule?.dependentModuleComposition),
    };
};

/** Request args for POST /customers/compositions/{originalId}. */
export const buildCatalogModuleWidthPatchRequest = ({
    compositionModule,
    width,
    templateCompositionId = null,
    draftCompositionId = null,
}) => {
    const data = buildCompositionModuleWidthPatchPayload(compositionModule, width);
    if (
        templateCompositionId == null
        || templateCompositionId === ""
        || data == null
    ) {
        return null;
    }

    return {
        originalId: String(templateCompositionId),
        id: draftCompositionId != null && draftCompositionId !== ""
            ? String(draftCompositionId)
            : null,
        data,
    };
};

export const CATALOG_WIDTH_RECALCULATING_INPUT_TOOLTIP =
    "После расчета размеров изменения станут доступны";

export const CATALOG_WIDTH_RECALCULATING_BUTTON_TOOLTIP =
    "После расчета размеров кнопка станет доступной";

export const CATALOG_RESET_BUTTON_TOOLTIP =
    "Возврат к исходным размерам кухонных модулей";

export const CATALOG_RESET_UNAVAILABLE_TOOLTIP =
    "Шаблон не был изменен";

export const CATALOG_CURRENT_PRICE_TOOLTIP =
    "Цена, сформированная для текущих размеров модулей";

/** Show «Текущая цена» when actualPrice is true or there is no draft (`second`). */
export const shouldShowCatalogCurrentPrice = ({actualPrice, hasSecond} = {}) =>
    actualPrice === true || !hasSecond;

const pickCatalogActualPriceFlag = (...candidates) => {
    for (const value of candidates) {
        if (value === true || value === false) {
            return value;
        }
    }
    return undefined;
};

/** Prefer root GET flag, then draft (`second`), then template (`first`). */
export const resolveCatalogActualPriceFlag = ({
    rootActualPrice,
    template,
    draft,
} = {}) =>
    pickCatalogActualPriceFlag(
        rootActualPrice,
        draft?.actualPrice,
        template?.actualPrice,
    );

export const formatCatalogPriceRub = (price, {from = true} = {}) => {
    if (price == null || Number.isNaN(Number(price))) {
        return null;
    }
    const prefix = from ? "от " : "";
    return `${prefix}${Number(price).toFixed(2)} руб.`;
};

const CATALOG_COMPOSITION_PATCH_KEYS = [
    "lengthLeft",
    "lengthRight",
    "minLengthLeft",
    "minLengthRight",
    "maxLengthLeft",
    "maxLengthRight",
    "height",
];

/** Composition fields recalculated on the server when module rows change (org settings / 3D). */
export const COMPOSITION_RECALCULATED_DIMENSION_KEYS = [
    "lengthLeft",
    "lengthRight",
    "lengthLeftTop",
    "lengthRightTop",
    "lengthLeftTableTop",
    "lengthRightTableTop",
    "height",
    "cornerType",
];

export const pickCompositionRecalculatedDimensions = (payload) => {
    if (payload == null || typeof payload !== "object") {
        return {};
    }
    const patch = {};
    for (const key of COMPOSITION_RECALCULATED_DIMENSION_KEYS) {
        if (Object.prototype.hasOwnProperty.call(payload, key)) {
            patch[key] = payload[key];
        }
    }
    return patch;
};

/** Module rows from GET (`moduleCompositions`) or POST (`modules`) payload. */
export const getModuleRowsFromCompositionPayload = (payload) => {
    if (payload == null) {
        return [];
    }
    if (Array.isArray(payload.moduleCompositions)) {
        return payload.moduleCompositions;
    }
    if (Array.isArray(payload.modules)) {
        return payload.modules;
    }
    return [];
};

export const unwrapCatalogPatchResponse = (responseData) => {
    if (responseData == null) {
        return null;
    }
    if (responseData.composition && typeof responseData.composition === "object") {
        const nested = responseData.composition;
        return {
            ...nested,
            customerId: responseData.customerId ?? nested.customerId,
            compositionId: responseData.compositionId ?? nested.compositionId,
            modules:
                responseData.modules ??
                nested.modules ??
                responseData.moduleCompositions ??
                nested.moduleCompositions,
            moduleCompositions:
                responseData.moduleCompositions ??
                nested.moduleCompositions,
            price: responseData.price ?? nested.price,
        };
    }
    return responseData;
};

/**
 * Draft composition id from POST /customers/compositions/{compositionId}
 * or POST /customers/compositions/{originalId}/create-order response body.
 */
export const extractCompositionIdFromResponse = (responseData) => {
    if (responseData == null || typeof responseData !== "object") {
        return null;
    }

    if (responseData.compositionId != null && responseData.compositionId !== "") {
        return String(responseData.compositionId);
    }

    return null;
};

/**
 * `customerId` from POST /customers/compositions/{compositionId}
 * or POST /customers/compositions/{originalId}/create-order response body.
 */
const pickCustomerId = (value) => {
    if (value == null || value === "") {
        return null;
    }
    return String(value);
};

export const extractCustomerIdFromResponse = (responseData) => {
    if (responseData == null || typeof responseData !== "object") {
        return null;
    }

    const fromRoot = pickCustomerId(responseData.customerId);
    if (fromRoot != null) {
        return fromRoot;
    }

    const fromComposition = pickCustomerId(responseData.composition?.customerId);
    if (fromComposition != null) {
        return fromComposition;
    }

    const payload = unwrapCatalogPatchResponse(responseData);
    return pickCustomerId(payload?.customerId);
};

export const syncStoredUidFromCustomerResponse = (responseData) => {
    const customerId = extractCustomerIdFromResponse(responseData);
    if (customerId != null) {
        syncCustomerIdToStoredUid(customerId);
    }
};

/** @deprecated use syncStoredUidFromCustomerResponse */
export const syncAnonymousKeyFromCustomerResponse = syncStoredUidFromCustomerResponse;

export const extractCatalogCompositionFieldsFromPatchResponse = (responseData) => {
    const payload = unwrapCatalogPatchResponse(responseData);
    if (payload == null) {
        return {};
    }
    return CATALOG_COMPOSITION_PATCH_KEYS.reduce((acc, key) => {
        if (payload[key] !== undefined) {
            acc[key] = payload[key];
        }
        return acc;
    }, {});
};

/** Rows to merge after catalog module width POST. */
export const extractModuleCompositionUpdatesFromPatchResponse = (responseData) => {
    const payload = unwrapCatalogPatchResponse(responseData);
    if (payload == null) {
        return [];
    }
    const moduleRows = getModuleRowsFromCompositionPayload(payload);
    if (moduleRows.length > 0) {
        return moduleRows;
    }
    if (Array.isArray(payload.updatedModuleCompositions)) {
        return payload.updatedModuleCompositions;
    }
    if (
        payload.id != null
        && payload.typeComposition == null
        && (payload.module != null || payload.width != null)
    ) {
        return [payload];
    }
    return [];
};

export const extractCatalogPriceFromPatchResponse = (responseData) => {
    const payload = unwrapCatalogPatchResponse(responseData);
    if (payload == null) {
        return undefined;
    }
    if (typeof payload.price === "number") {
        return payload.price;
    }
    return undefined;
};

/** Catalog composition GET: `[template, draft?]`, `{ template, draft }`, or `{ first, second }`. */
export const parseCatalogItemGetResponse = (responseData) => {
    if (responseData == null) {
        return {template: null, draft: null, actualPrice: undefined};
    }
    if (Array.isArray(responseData)) {
        const template = responseData[0] ?? null;
        const draft = responseData[1] ?? null;
        return {
            template,
            draft,
            actualPrice: resolveCatalogActualPriceFlag({template, draft}),
        };
    }
    if (typeof responseData === "object") {
        const rootActualPrice = responseData.actualPrice;
        if ("first" in responseData || "second" in responseData) {
            const template = responseData.first ?? null;
            const draft = responseData.second ?? null;
            return {
                template,
                draft,
                actualPrice: resolveCatalogActualPriceFlag({
                    rootActualPrice,
                    template,
                    draft,
                }),
            };
        }
        if ("template" in responseData || "draft" in responseData) {
            const template = responseData.template ?? null;
            const draft = responseData.draft ?? null;
            return {
                template,
                draft,
                actualPrice: resolveCatalogActualPriceFlag({
                    rootActualPrice,
                    template,
                    draft,
                }),
            };
        }
    }
    return {
        template: responseData,
        draft: null,
        actualPrice: resolveCatalogActualPriceFlag({
            rootActualPrice: responseData?.actualPrice,
            template: responseData,
        }),
    };
};

export const extractCatalogCompositionDraftId = (draft) => {
    if (draft?.id == null || draft.id === "") {
        return null;
    }
    return String(draft.id);
};

/** Kitchen dimension fields present in the draft (non-null only). */
export const extractCatalogDraftDimensionOverlay = (draft) => {
    if (draft == null) {
        return {};
    }
    return CATALOG_COMPOSITION_PATCH_KEYS.reduce((acc, key) => {
        if (draft[key] != null) {
            acc[key] = draft[key];
        }
        return acc;
    }, {});
};

const CATALOG_MODULE_SIZE_FIELDS = ["width", "height", "depth"];

const buildCatalogModuleSizeOverlay = (sourceRow) =>
    CATALOG_MODULE_SIZE_FIELDS.reduce((acc, field) => {
        const value = getModuleCompositionDimension(sourceRow, field);
        if (value != null) {
            acc[field] = value;
        }
        return acc;
    }, {});

/** Синхронизирует width/height/depth на строке и во вложенном module (источник — module). */
export const applyModuleCompositionSizeOverlay = (row, sizeOverlay) => {
    if (row == null || sizeOverlay == null || typeof sizeOverlay !== "object") {
        return row;
    }
    const keys = Object.keys(sizeOverlay);
    if (keys.length === 0) {
        return row;
    }
    const module =
        row.module && typeof row.module === "object"
            ? {...row.module, ...sizeOverlay}
            : row.module;
    return {...row, ...sizeOverlay, ...(module != null ? {module} : {})};
};

/** Откат ширины в UI после ошибки POST (row + вложенный module). */
export const revertModuleCompositionRowWidth = (row, width) => {
    if (row == null) {
        return row;
    }
    if (width == null || width === "") {
        return applyModuleCompositionSizeOverlay(row, {width: null});
    }
    const numericWidth = Number(width);
    return applyModuleCompositionSizeOverlay(row, {
        width: Number.isFinite(numericWidth) ? numericWidth : width,
    });
};

const getModuleCompositionPlacementKey = (row) => {
    const priority = row?.priority ?? row?.module?.priority;
    const sideBaseModule = row?.sideBaseModule ?? row?.module?.sideBaseModule;
    const heightLocation = row?.heightLocation ?? row?.module?.heightLocation;
    if (priority == null || sideBaseModule == null || heightLocation == null) {
        return null;
    }
    return `${priority}|${sideBaseModule}|${heightLocation}`;
};

const getModuleCompositionPlanPoint = (row) => {
    const x = Number(row?.coordinateXPoint);
    const y = Number(row?.coordinateYPoint);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
    }
    return {x, y};
};

const moduleCompositionPlanDistance = (templateRow, draftRow) => {
    const templatePoint = getModuleCompositionPlanPoint(templateRow);
    const draftPoint = getModuleCompositionPlanPoint(draftRow);
    if (!templatePoint || !draftPoint) {
        return Number.POSITIVE_INFINITY;
    }
    return Math.hypot(
        templatePoint.x - draftPoint.x,
        templatePoint.y - draftPoint.y,
    );
};

/** Связь строки `second` → id строки шаблона в `first` (только merge 3D, без `row.originalId`). */
const resolveCustomerDraftModuleTemplateRowId = (draftRow) => {
    const templateModuleId = getOriginalModuleCompositionId(draftRow);
    if (templateModuleId != null) {
        return templateModuleId;
    }
    if (draftRow?.originalModuleCompositionId != null && draftRow.originalModuleCompositionId !== "") {
        return draftRow.originalModuleCompositionId;
    }
    return null;
};

/** pacedModule в GET /3d — на строке или в module.pacedModule. */
const resolveModuleRowPacedModule = (row) =>
    row?.pacedModule ?? row?.module?.pacedModule ?? null;

const placementPointLooksUsable = (point) => {
    if (point == null || typeof point !== "object") {
        return false;
    }
    return point.x != null || point.y != null || point.z != null;
};

/** Есть ли поза для отрисовки модуля на сцене (anchorPoint / localPosition). */
const moduleRowHasScenePlacement = (row) => {
    const paced = resolveModuleRowPacedModule(row);
    if (paced == null || typeof paced !== "object") {
        return false;
    }
    return placementPointLooksUsable(paced.localPosition ?? paced.anchorPoint);
};

const detailHasScenePlacement = (detail) => {
    const paced = detail?.pacedDetailModule;
    if (paced == null || typeof paced !== "object") {
        return false;
    }
    return placementPointLooksUsable(paced.localPosition ?? paced.anchorPoint);
};

const getModuleRowDetailPatterns = (row) => {
    if (Array.isArray(row?.module?.detailPatterns)) {
        return row.module.detailPatterns;
    }
    if (Array.isArray(row?.detailPatterns)) {
        return row.detailPatterns;
    }
    return null;
};

const withModuleRowDetailPatterns = (row, detailPatterns) => {
    if (row?.module != null && typeof row.module === "object") {
        return {
            ...row,
            module: {
                ...row.module,
                detailPatterns,
            },
        };
    }
    return {
        ...row,
        detailPatterns,
    };
};

const getDetailPlacementMatchKey = (detail, index = 0) => {
    if (detail?.position != null && detail.position !== "") {
        return `position:${detail.position}`;
    }
    if (detail?.compositionDetailId != null && detail.compositionDetailId !== "") {
        return `compositionDetailId:${detail.compositionDetailId}`;
    }
    if (detail?.id != null && detail.id !== "") {
        return `id:${detail.id}`;
    }
    if (detail?.name != null && detail.name !== "") {
        return `name:${detail.name}`;
    }
    return `index:${index}`;
};

const flattenDetailPatternsForPlacement = (patterns) => {
    const list = [];
    if (!Array.isArray(patterns)) {
        return list;
    }
    for (const pattern of patterns) {
        const details = Array.isArray(pattern?.details) ? pattern.details : [];
        for (const detail of details) {
            if (detail != null && typeof detail === "object") {
                list.push(detail);
            }
        }
    }
    return list;
};

/**
 * POST ширины часто отдаёт moduleCompositions без pacedModule.
 * Без сохранения позы со сцены hydrate снимает модули (inst = null).
 */
const preserveScenePlacementOnDraftModuleRow = (sceneRow, draftMergedRow) => {
    if (moduleRowHasScenePlacement(draftMergedRow)) {
        return draftMergedRow;
    }
    const scenePaced = resolveModuleRowPacedModule(sceneRow);
    if (scenePaced == null) {
        return draftMergedRow;
    }
    return {
        ...draftMergedRow,
        pacedModule: scenePaced,
    };
};

/**
 * В customer preview зелёные кубы скрыты — видны только детали.
 * POST ширины часто отдаёт новые detailPatterns без pacedDetailModule → шкафы «пропадают».
 */
const preserveSceneDetailPlacementsOnDraftModuleRow = (sceneRow, draftMergedRow) => {
    const scenePatterns = getModuleRowDetailPatterns(sceneRow);
    const draftPatterns = getModuleRowDetailPatterns(draftMergedRow);
    if (!Array.isArray(scenePatterns) || scenePatterns.length === 0) {
        return draftMergedRow;
    }
    if (!Array.isArray(draftPatterns) || draftPatterns.length === 0) {
        return withModuleRowDetailPatterns(draftMergedRow, scenePatterns);
    }

    const sceneDetails = flattenDetailPatternsForPlacement(scenePatterns);
    const sceneByKey = new Map();
    sceneDetails.forEach((detail, index) => {
        const key = getDetailPlacementMatchKey(detail, index);
        if (!sceneByKey.has(key)) {
            sceneByKey.set(key, detail);
        }
    });

    let flatIndex = 0;
    const nextPatterns = draftPatterns.map((pattern) => {
        const details = Array.isArray(pattern?.details) ? pattern.details : [];
        return {
            ...pattern,
            details: details.map((detail) => {
                const index = flatIndex;
                flatIndex += 1;
                if (detail == null || typeof detail !== "object" || detailHasScenePlacement(detail)) {
                    return detail;
                }
                const sceneDetail = sceneByKey.get(getDetailPlacementMatchKey(detail, index))
                    ?? sceneDetails[index]
                    ?? null;
                if (sceneDetail?.pacedDetailModule == null) {
                    return detail;
                }
                return {
                    ...detail,
                    pacedDetailModule: sceneDetail.pacedDetailModule,
                };
            }),
        };
    });

    return withModuleRowDetailPatterns(draftMergedRow, nextPatterns);
};

/**
 * Переносит позу модуля и pacedDetailModule деталей со сцены на обновлённую строку
 * (новые габариты после POST ширины).
 */
export const mergeCompositionModuleRowKeepingScenePlacements = (sceneRow, nextRow) => {
    if (nextRow == null) {
        return sceneRow;
    }
    if (sceneRow == null) {
        return nextRow;
    }
    return preserveSceneDetailPlacementsOnDraftModuleRow(
        sceneRow,
        preserveScenePlacementOnDraftModuleRow(sceneRow, nextRow),
    );
};

const applyCustomerDraftModuleRowFor3dScene = (templateRow, draftRow) => {
    if (!draftRow) {
        return templateRow;
    }
    const templateId = normalizeModuleCompositionId(templateRow.id);
    const draftRowId = normalizeModuleCompositionId(draftRow.id);
    const merged = {
        ...draftRow,
        id: templateRow.id,
        ...(draftRowId != null && draftRowId !== templateId
            ? {[CATALOG_DRAFT_MODULE_COMPOSITION_ID_FIELD]: draftRow.id}
            : {}),
    };
    return mergeCompositionModuleRowKeepingScenePlacements(templateRow, merged);
};

/**
 * Сопоставление строк second → first:
 * originalModuleComposition / originalModuleCompositionId → module.id →
 * priority|side|heightLocation (+ ближайшая точка плана).
 */
const createCatalogDraftModuleMatcher = (draftModules) => {
    const draftList = Array.isArray(draftModules) ? draftModules : [];
    const draftByTemplateRowId = new Map();
    const draftByModuleId = new Map();
    const draftsByPlacementKey = new Map();
    for (const draftRow of draftList) {
        const templateRowId = resolveCustomerDraftModuleTemplateRowId(draftRow);
        if (templateRowId != null) {
            draftByTemplateRowId.set(
                normalizeModuleCompositionId(templateRowId),
                draftRow,
            );
        }
        // Уже смерженные строки /3d имеют id шаблона — тоже ключ для добора с карточки.
        const ownId = normalizeModuleCompositionId(draftRow?.id);
        if (ownId != null && !draftByTemplateRowId.has(ownId)) {
            draftByTemplateRowId.set(ownId, draftRow);
        }
        const moduleId = normalizeModuleCompositionId(draftRow?.module?.id);
        if (moduleId != null && !draftByModuleId.has(moduleId)) {
            draftByModuleId.set(moduleId, draftRow);
        }
        const placementKey = getModuleCompositionPlacementKey(draftRow);
        if (placementKey != null) {
            const bucket = draftsByPlacementKey.get(placementKey) ?? [];
            bucket.push(draftRow);
            draftsByPlacementKey.set(placementKey, bucket);
        }
    }

    const consumedDraftIds = new Set();

    const takeDraftRow = (draftRow) => {
        if (!draftRow) {
            return null;
        }
        const draftId = normalizeModuleCompositionId(draftRow.id);
        if (draftId != null && consumedDraftIds.has(draftId)) {
            return null;
        }
        if (draftId != null) {
            consumedDraftIds.add(draftId);
        }
        return draftRow;
    };

    return (templateRow) => {
        const fromTemplateId = draftByTemplateRowId.get(
            normalizeModuleCompositionId(templateRow.id),
        );
        const takenFromTemplateId = takeDraftRow(fromTemplateId);
        if (takenFromTemplateId) {
            return takenFromTemplateId;
        }

        const fromModuleId = draftByModuleId.get(
            normalizeModuleCompositionId(templateRow?.module?.id),
        );
        const takenFromModuleId = takeDraftRow(fromModuleId);
        if (takenFromModuleId) {
            return takenFromModuleId;
        }

        const placementKey = getModuleCompositionPlacementKey(templateRow);
        if (placementKey == null) {
            return null;
        }
        const candidates = (draftsByPlacementKey.get(placementKey) ?? []).filter(
            (draftRow) => {
                const draftId = normalizeModuleCompositionId(draftRow.id);
                return draftId == null || !consumedDraftIds.has(draftId);
            },
        );
        if (candidates.length === 0) {
            return null;
        }
        let bestDraft = candidates[0];
        let bestDistance = moduleCompositionPlanDistance(templateRow, bestDraft);
        for (let index = 1; index < candidates.length; index += 1) {
            const candidate = candidates[index];
            const distance = moduleCompositionPlanDistance(templateRow, candidate);
            if (distance < bestDistance) {
                bestDraft = candidate;
                bestDistance = distance;
            }
        }
        return takeDraftRow(bestDraft);
    };
};

/** Карточка: полный first, размеры/мета из second (в т.ч. без originalModuleComposition). */
export const mergeCatalogTemplateModules = (templateModules, draftModules) => {
    const templateList = Array.isArray(templateModules) ? templateModules : [];
    const draftList = Array.isArray(draftModules) ? draftModules : [];
    if (draftList.length === 0) {
        return templateList;
    }
    const resolveDraftForTemplateRow = createCatalogDraftModuleMatcher(draftList);
    return templateList.map((row) => {
        const draftRow = resolveDraftForTemplateRow(row);
        if (!draftRow) {
            return row;
        }
        const sizeOverlay = buildCatalogModuleSizeOverlay(draftRow);
        return applyModuleCompositionSizeOverlay(
            {
                ...row,
                ...(draftRow.id != null
                    ? {[CATALOG_DRAFT_MODULE_COMPOSITION_ID_FIELD]: draftRow.id}
                    : {}),
            },
            sizeOverlay,
        );
    });
};

/**
 * 3D-сцена: полный ряд из second для модулей заказчика, остальные — из first (как логика «где есть draft — customer»).
 */
export const mergeCatalogTemplateModulesFor3dScene = (templateModules, draftModules) => {
    const templateList = Array.isArray(templateModules) ? templateModules : [];
    const draftList = Array.isArray(draftModules) ? draftModules : [];
    if (draftList.length === 0) {
        return templateList;
    }
    const resolveDraftForTemplateRow = createCatalogDraftModuleMatcher(draftList);
    return templateList.map((templateRow) =>
        applyCustomerDraftModuleRowFor3dScene(
            templateRow,
            resolveDraftForTemplateRow(templateRow),
        ),
    );
};

/**
 * Добор шкафов с карточки к ответу GET /3d без пересборки строк сцены:
 * matched → целиком строка /3d (detailPatterns + текстуры), missing → строка карточки.
 */
export const restoreCustomer3dSceneModulesFromCard = (cardModules, sceneModules) => {
    const cardList = Array.isArray(cardModules) ? cardModules : [];
    const sceneList = Array.isArray(sceneModules) ? sceneModules : [];
    if (sceneList.length === 0) {
        return cardList;
    }
    if (cardList.length <= sceneList.length) {
        return sceneList;
    }
    const resolveSceneForCard = createCatalogDraftModuleMatcher(sceneList);
    return cardList.map((cardRow) => {
        const sceneRow = resolveSceneForCard(cardRow);
        if (!sceneRow) {
            return cardRow;
        }
        const cardId = normalizeModuleCompositionId(cardRow.id);
        const sceneId = normalizeModuleCompositionId(sceneRow.id);
        if (cardId == null || cardId === sceneId) {
            return sceneRow;
        }
        return {
            ...sceneRow,
            id: cardRow.id,
            [CATALOG_DRAFT_MODULE_COMPOSITION_ID_FIELD]:
                sceneRow[CATALOG_DRAFT_MODULE_COMPOSITION_ID_FIELD] ?? sceneRow.id,
        };
    });
};

/** Обновление payload сцены GET /3d из ответа POST модуля (размеры, moduleCompositions). */
export const applyCustomer3dSceneCompositionPatch = (sceneComposition, responseData) => {
    if (sceneComposition == null) {
        return sceneComposition;
    }
    const payload = unwrapCatalogPatchResponse(responseData);
    if (payload == null) {
        return sceneComposition;
    }
    const compositionPatch = extractCatalogCompositionFieldsFromPatchResponse(payload);
    const patchList = extractModuleCompositionUpdatesFromPatchResponse(responseData);
    const currentRows = getModuleRowsFromCompositionPayload(sceneComposition);
    if (patchList.length === 0) {
        return {
            ...sceneComposition,
            ...compositionPatch,
        };
    }
    // POST update отдаёт draft-id без originalModuleComposition — матчим как GET /3d
    // (id / draftModuleCompositionId / module.id / priority|side|height).
    const nextModuleRows = mergeCatalogTemplateModulesFor3dScene(currentRows, patchList);
    return {
        ...sceneComposition,
        ...compositionPatch,
        moduleCompositions: nextModuleRows,
        texturedPictures: sceneComposition.texturedPictures,
    };
};

/** Merge catalog module rows after width POST; keeps template id and dependency links. */
export const mergeCatalogModuleCompositionUpdates = (modules, updates) => {
    const list = Array.isArray(modules) ? modules : [];
    const patchList = Array.isArray(updates) ? updates : [];
    if (patchList.length === 0) {
        return list;
    }
    // Как для 3D: draft-id без originalModuleComposition — через placement/module.id.
    const resolvePatch = createCatalogDraftModuleMatcher(patchList);

    return list.map((row) => {
        const patch = resolvePatch(row);
        if (!patch) {
            return row;
        }

        const sizeOverlay = buildCatalogModuleSizeOverlay(patch);

        const patchId = patch.id;
        const templateId = normalizeModuleCompositionId(row.id);
        const customerModuleId = normalizeModuleCompositionId(patchId);
        const nextDraftId =
            customerModuleId != null && customerModuleId !== templateId
                ? customerModuleId
                : row[CATALOG_DRAFT_MODULE_COMPOSITION_ID_FIELD];

        return applyModuleCompositionSizeOverlay(
            {
                ...row,
                ...(nextDraftId != null
                    ? {[CATALOG_DRAFT_MODULE_COMPOSITION_ID_FIELD]: nextDraftId}
                    : {}),
            },
            sizeOverlay,
        );
    });
};

/** Partial overlay: only draft fields that are set replace template values. */
export const mergeCatalogTemplateWithDraft = (template, draft) => {
    if (!template) {
        return null;
    }
    if (!draft) {
        return template;
    }

    const dimensionOverlay = extractCatalogDraftDimensionOverlay(draft);
    const draftModules = getModuleRowsFromCompositionPayload(draft);
    const templateModules = getModuleRowsFromCompositionPayload(template);

    return {
        ...template,
        ...dimensionOverlay,
        ...(draft.price != null ? {price: draft.price} : {}),
        ...(draft.actualPrice === true || draft.actualPrice === false
            ? {actualPrice: draft.actualPrice}
            : {}),
        ...(draftModules.length > 0
            ? {moduleCompositions: mergeCatalogTemplateModules(templateModules, draftModules)}
            : {}),
    };
};

/**
 * Template for display; draft id for module POST when a draft row exists in GET.
 */
export const resolveCatalogItemCompositionFromGet = (template, draft) => {
    const draftId = extractCatalogCompositionDraftId(draft);

    if (!template) {
        return {composition: null, draftId};
    }

    if (!draftId) {
        return {composition: template, draftId: null};
    }

    return {
        composition: mergeCatalogTemplateWithDraft(template, draft),
        draftId,
    };
};

/** GET /customers/compositions/{id}/3d — first + customer rows from second для 3D (не только width overlay). */
export const resolveCustomerCompositionSceneFrom3dGet = (responseData) => {
    const {template, draft} = parseCatalogItemGetResponse(responseData);
    if (!template) {
        return null;
    }
    if (!draft) {
        return template;
    }
    const dimensionOverlay = extractCatalogDraftDimensionOverlay(draft);
    const draftModules = getModuleRowsFromCompositionPayload(draft);
    const templateModules = getModuleRowsFromCompositionPayload(template);
    return {
        ...template,
        ...dimensionOverlay,
        ...(draft.price != null ? {price: draft.price} : {}),
        moduleCompositions: mergeCatalogTemplateModulesFor3dScene(
            templateModules,
            draftModules,
        ),
    };
};

/**
 * Order card GET /customers/orders/{id}/with-composition:
 * compositions — { first, second } (or legacy array / { template, draft }).
 */
export const resolveCatalogItemCompositionFromOrderGet = (orderPayload) => {
    const {
        template,
        draft,
        actualPrice: compositionsActualPrice,
    } = parseCatalogItemGetResponse(orderPayload?.compositions);
    const {composition, draftId} = resolveCatalogItemCompositionFromGet(template, draft);
    const templateCompositionId = resolveCatalogModulePatchQueryOriginalId(template);
    const draftCompositionId = resolveCatalogModulePatchQueryId(draft);
    const actualPrice = resolveCatalogActualPriceFlag({
        rootActualPrice: orderPayload?.actualPrice ?? compositionsActualPrice,
        template,
        draft,
    });

    return {
        orderId: resolveCustomerOrderId(orderPayload),
        template,
        draft,
        composition,
        draftId,
        templateCompositionId,
        draftCompositionId,
        draftPathCompositionId: draftCompositionId,
        actualPrice,
        hasSecond: draft != null,
    };
};

/** True when GET with-composition includes a template composition to display. */
export const isOrderWithCompositionReady = (orderPayload) => {
    const {template} = parseCatalogItemGetResponse(orderPayload?.compositions);
    return template?.id != null;
};

export const CUSTOMER_ORDER_CREATED_MESSAGE = "Заказ создан";

const CUSTOMER_ORDER_CREATED_FLASH_KEY = "customerOrderCreatedFlash";

export const markCustomerOrderCreatedFlash = () => {
    try {
        sessionStorage.setItem(CUSTOMER_ORDER_CREATED_FLASH_KEY, "1");
    } catch {
        // ignore
    }
};

export const consumeCustomerOrderCreatedFlash = () => {
    try {
        if (sessionStorage.getItem(CUSTOMER_ORDER_CREATED_FLASH_KEY) !== "1") {
            return false;
        }
        sessionStorage.removeItem(CUSTOMER_ORDER_CREATED_FLASH_KEY);
        return true;
    } catch {
        return false;
    }
};

/**
 * Order id from CustomerOrderDto (POST create-order → orderId; GET list / with-composition → id).
 */
export const resolveCustomerOrderId = (order) => {
    const id = order?.orderId ?? order?.id;
    if (id == null || id === "") {
        return null;
    }
    return id;
};

/** Composition id for POST /customers/compositions module width patch (`second` query `id`). */
export const resolveCatalogCompositionPatchKitchenId = (template, draft) =>
    resolveCatalogModulePatchQueryId(draft);

/** Composition id for POST /customers/compositions/{originalId}/create-order (path). */
export const resolveCatalogCompositionOrderId = (template, draft) => {
    if (draft?.originalId != null && draft.originalId !== "") {
        return draft.originalId;
    }
    if (template?.id != null && template.id !== "") {
        return template.id;
    }
    return null;
};

/** Query params for POST /customers/compositions/{originalId}/create-order. */
export const buildCreateOrderFromCompositionParams = ({
    originalCompositionId,
    draftId,
}) => {
    if (originalCompositionId == null || originalCompositionId === "") {
        return null;
    }
    return {
        originalId: originalCompositionId,
        id: draftId != null && draftId !== "" ? draftId : null,
    };
};

export const isCatalogCompositionChanged = ({
    moduleCompositions,
    compositionData,
    initialSnapshot,
    compositionDraftId,
}) => {
    if (compositionDraftId != null) {
        return true;
    }
    if (!initialSnapshot) {
        return false;
    }

    const snapshotModules = initialSnapshot.moduleCompositions ?? [];
    const currentModules = Array.isArray(moduleCompositions) ? moduleCompositions : [];
    if (currentModules.length !== snapshotModules.length) {
        return true;
    }

    const snapshotById = new Map(
        snapshotModules.map((row) => [String(row.id), row]),
    );
    for (const row of currentModules) {
        const baseline = snapshotById.get(String(row.id));
        if (!baseline) {
            return true;
        }
        if (
            (getModuleCompositionDimension(row, "width") ?? null)
            !== (getModuleCompositionDimension(baseline, "width") ?? null)
        ) {
            return true;
        }
    }

    if ((compositionData?.price ?? null) !== (initialSnapshot.price ?? null)) {
        return true;
    }

    const currentDimensions = extractCatalogCompositionFieldsFromPatchResponse(compositionData);
    const snapshotDimensions = initialSnapshot.dimensions ?? {};
    return Object.keys({...currentDimensions, ...snapshotDimensions}).some(
        (key) => (currentDimensions[key] ?? null) !== (snapshotDimensions[key] ?? null),
    );
};

/** Customer draft composition id from module width POST (path for next POST). */
export const extractCatalogCompositionDraftIdFromPatchResponse = (responseData) => {
    const compositionId = extractCompositionIdFromResponse(responseData);
    if (compositionId != null) {
        return compositionId;
    }

    const payload = unwrapCatalogPatchResponse(responseData);
    if (payload?.id == null) {
        return undefined;
    }
    if (
        payload.typeComposition != null ||
        getModuleRowsFromCompositionPayload(payload).length > 0
    ) {
        return String(payload.id);
    }
    return undefined;
};

export const mergeModuleCompositionUpdates = (modules, updates) => {
    const list = Array.isArray(modules) ? modules : [];
    const patchList = Array.isArray(updates) ? updates : [];
    if (patchList.length === 0) {
        return list;
    }
    const byId = new Map(
        patchList.map((row) => [normalizeModuleCompositionId(row.id), row]),
    );
    return list.map((row) => {
        const patch = byId.get(normalizeModuleCompositionId(row.id));
        return patch ? {...row, ...patch} : row;
    });
};

export const buildCompositionModulePatchPayload = (
    compositionModule,
    {width, coordinateXPoint, coordinateYPoint} = {},
) =>
    buildOrganizationCompositionModulePatchBody(compositionModule, {
        ...(width !== undefined ? {width} : {}),
        ...(coordinateXPoint !== undefined ? {coordinateXPoint} : {}),
        ...(coordinateYPoint !== undefined ? {coordinateYPoint} : {}),
    });

/** Rows in corner modules section: label + module field (`width` | `depth`). */
export const getCornerModuleRowConfigs = (cornerType) => {
    if (cornerType === "CORNER_LEFT") {
        return [
            {label: "Слева", sizeField: "depth"},
            {label: "Справа", sizeField: "width"},
        ];
    }
    if (cornerType === "CORNER_RIGHT") {
        return [
            {label: "Слева", sizeField: "width"},
            {label: "Справа", sizeField: "depth"},
        ];
    }
    return [
        {label: "Ширина", sizeField: "width"},
        {label: "Глубина", sizeField: "depth"},
    ];
};

/** Corner side for a specific corner-module row; falls back to kitchen corner type. */
export const resolveCornerTypeForCornerModuleRow = (row, fallbackCornerType) => {
    const normalizeCornerSide = (value) => {
        if (value === "CORNER_LEFT" || value === "CORNER__LEFT") {
            return "CORNER_LEFT";
        }
        if (value === "CORNER_RIGHT" || value === "CORNER__RIGHT") {
            return "CORNER_RIGHT";
        }
        return null;
    };

    return (
        normalizeCornerSide(row?.sideBaseModule)
        ?? normalizeCornerSide(row?.module?.sideBaseModule)
        ?? fallbackCornerType
    );
};

export const getCornerModulePrimaryRowConfig = (cornerType) =>
    getCornerModuleRowConfigs(cornerType)[0];

export const getCornerModuleSecondaryRowConfig = (cornerType) =>
    getCornerModuleRowConfigs(cornerType)[1];

/** @deprecated use getCornerModulePrimaryRowConfig */
export const getCornerModulePrimaryWidthLabel = (cornerType) =>
    getCornerModulePrimaryRowConfig(cornerType).label;

/** @deprecated use getCornerModuleSecondaryRowConfig */
export const getCornerModuleSecondaryRowLabel = (cornerType) =>
    getCornerModuleSecondaryRowConfig(cornerType).label;

/** Label for module row width field in left/right media sections (tab «Список шкафов»). */
export const getMediaModuleWidthLabel = (cornerType, side) => {
    if (cornerType === "STRAIGHT") {
        return "Длина";
    }
    return side === "left" ? "Слева" : "Справа";
};

export const buildCatalogDimensionBlocks = (cornerType, lengthLeft, lengthRight) => {
    if (cornerType === "STRAIGHT") {
        return [{label: "Длина, мм", value: lengthLeft}];
    }
    return [
        {label: "Левая сторона, мм", value: lengthLeft},
        {label: "Правая сторона, мм", value: lengthRight},
    ];
};

export const getCatalogDimensionCardCompactParts = (label, value) => {
    const name = label.replace(/,\s*мм\s*$/i, "").trim();
    const displayValue = value == null || value === "" ? "—" : value;
    return {
        name,
        displayValue,
    };
};

export const formatCompositionSizeMm = (item) => {
    const parts = buildCatalogDimensionBlocks(
        item?.cornerType,
        item?.lengthLeft,
        item?.lengthRight,
    ).map(({value}) => value);
    if (item?.height != null) {
        parts.push(item.height);
    }
    return parts.join(" * ");
};

const formatMmAsCm = (valueMm) => {
    const numeric = Number(valueMm);
    if (Number.isNaN(numeric)) {
        return "—";
    }
    const cm = numeric / 10;
    return Number.isInteger(cm) ? String(cm) : String(Number(cm.toFixed(1)));
};

export const formatCatalogSizeRangeCm = (fromMm, toMm) => {
    const from = formatMmAsCm(fromMm);
    const to = formatMmAsCm(toMm);
    return `от ${from} см до ${to} см`;
};

export const formatCatalogCardSizeValueCm = (valueMm) => {
    const cm = formatMmAsCm(valueMm);
    return cm === "—" ? "—" : `${cm} см`;
};

/** Size lines for draft cards: current lengthLeft / lengthRight without min–max range. */
export const formatCatalogDraftCardSizeLines = (item) => {
    const cornerType = item?.cornerType;

    if (cornerType === "STRAIGHT") {
        return [{
            label: "Длина",
            range: formatCatalogCardSizeValueCm(item?.lengthLeft),
        }];
    }

    return [
        {
            label: "Левая сторона",
            range: formatCatalogCardSizeValueCm(item?.lengthLeft),
        },
        {
            label: "Правая сторона",
            range: formatCatalogCardSizeValueCm(item?.lengthRight),
        },
    ];
};

/** First rows for catalog item tab «Основная информация» → «Характеристика». */
export const getCatalogCharacteristicDimensionRows = (item) => {
    if (!item) {
        return [];
    }
    const leftRange = formatCatalogSizeRangeCm(item.minLengthLeft, item.maxLengthLeft);
    if (item.cornerType === "STRAIGHT") {
        return [{text: "Размерный ряд", value: leftRange}];
    }
    const rightRange = formatCatalogSizeRangeCm(item.minLengthRight, item.maxLengthRight);
    return [{
        text: "Размерный ряд",
        value: `${leftRange} / ${rightRange}`,
    }];
};

const getCatalogCardSizeRangeMm = (item, side) => {
    if (side === "left") {
        return {
            from: item?.minLengthLeft,
            to: item?.maxLengthLeft,
        };
    }

    return {
        from: item?.minLengthRight,
        to: item?.maxLengthRight,
    };
};

/** Top/bottom short names for catalog card (Корпус / Фасад). */
export const formatCompositionShortTopBottom = (top, bottom) => {
    if (top != null && bottom != null && top !== bottom) {
        return `${top} / ${bottom}`;
    }
    return top ?? bottom ?? "";
};

/** Size lines for catalog product cards (ranges in cm). */
export const formatCatalogCardSizeLines = (item) => {
    const cornerType = item?.cornerType;

    if (cornerType === "STRAIGHT") {
        const {from, to} = getCatalogCardSizeRangeMm(item, "left");
        return [{
            label: "Длина",
            range: formatCatalogSizeRangeCm(from, to),
        }];
    }

    const left = getCatalogCardSizeRangeMm(item, "left");
    const right = getCatalogCardSizeRangeMm(item, "right");

    return [
        {
            label: "Левая сторона",
            range: formatCatalogSizeRangeCm(left.from, left.to),
        },
        {
            label: "Правая сторона",
            range: formatCatalogSizeRangeCm(right.from, right.to),
        },
    ];
};
